import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitHeading from "@/components/common/SplitHeading";

const steps = [
  ["01", "Discovery Call", "We listen. Understand your business, goals & roadblocks in 30 minutes."],
  ["02", "Business Audit", "We map your funnel, tools and team — surfacing exactly where AI wins."],
  ["03", "AI Strategy", "A custom 30-60-90 day plan with measurable KPIs you actually care about."],
  ["04", "Design", "We craft premium interfaces & journeys that feel effortless to use."],
  ["05", "Development", "Engineers build the AI workflows, integrations and dashboards."],
  ["06", "Automation", "Live AI agents — WhatsApp, email, CRM, support — connected to your stack."],
  ["07", "Testing", "Real-world QA with edge-cases, load tests and human-in-the-loop reviews."],
  ["08", "Launch", "Soft-launch to a segment, then ramp confidently to the full audience."],
  ["09", "Continuous Support", "Monthly reviews, optimizations and new automations — we stay in your corner."],
];

export default function Process() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const lineRef = useRef(null);
  const stepsRef = useRef([]);

  useEffect(() => {
    if (!sectionRef.current) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );

      gsap.fromTo(
        stepsRef.current.filter(Boolean),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
          },
        }
      );

      gsap.fromTo(
        lineRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          duration: 1.5,
          ease: "power2.out",
          transformOrigin: "top",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="process" ref={sectionRef} className="section relative" data-testid="process-section">
      <div className="container-x relative">
        <div ref={headerRef} className="text-center max-w-3xl mx-auto" style={{ opacity: 0 }}>
          <span className="eyebrow"><span className="dot" /> Process</span>
          <SplitHeading
            as="h2"
            className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]"
            type="words"
            y={50}
            stagger={0.04}
            start="top 85%"
          >
            From first call to <span className="font-serif italic gradient-text">launch</span> — in weeks, not months.
          </SplitHeading>
        </div>

        <div className="relative mt-16 max-w-4xl mx-auto">
          {/* Center glowing line */}
          <div className="absolute left-4 sm:left-1/2 sm:-translate-x-1/2 top-0 bottom-0 w-px">
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent, var(--glass-border), transparent)" }} />
            <div
              ref={lineRef}
              className="absolute left-0 top-0 w-px h-full"
              style={{
                background: "linear-gradient(to bottom, var(--brand), transparent)",
                boxShadow: "0 0 12px var(--brand-glow)",
                transform: "scaleY(0)",
              }}
            />
          </div>

          <ol className="space-y-10">
            {steps.map(([num, title, text], i) => (
              <li
                key={num}
                ref={(el) => (stepsRef.current[i] = el)}
                className={`relative grid sm:grid-cols-2 gap-6 ${i % 2 ? "sm:[&>*:first-child]:order-2" : ""}`}
                data-testid={`process-step-${i}`}
                style={{ opacity: 0 }}
              >
                {/* Glowing node */}
                <span
                  className="absolute left-4 sm:left-1/2 sm:-translate-x-1/2 -top-1 h-3 w-3 rounded-full node-pulse"
                  style={{ background: "var(--brand)" }}
                />

                <div className={`pl-12 sm:pl-0 ${i % 2 ? "sm:pl-12 sm:pr-0 sm:text-left" : "sm:pr-12 sm:text-right"}`}>
                  <div className="text-xs font-mono tracking-widest text-[var(--brand)]">{num}</div>
                  <h3 className="mt-1 text-2xl text-[var(--body-fg)] font-semibold tracking-tight">{title}</h3>
                </div>
                <div className={`pl-12 sm:pl-0 ${i % 2 ? "sm:pr-12 sm:text-right" : "sm:pl-12"}`}>
                  <div className="glass rounded-2xl p-5 tilt-card glow-border hover:bg-white/60 transition-colors">
                    <p className="text-sm text-[var(--body-fg)]/70 leading-relaxed">{text}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
